import { createContext, useCallback, useContext, useState } from 'react';
import { useAuthenticatedApiContext } from '../api/useApiClient';
import { CEMutationResponse, TaniumInstance } from '../types';
import { AsyncState, useAsync } from './useAsync';
import { useAsyncEffect } from './useAsyncEffect';

interface TaniumInstancesCtx {
  linkedTaniumInstances: TaniumInstance[];
  loadLinkedTaniumInstances: AsyncState<void, () => Promise<void>>;
  deactivateCE: AsyncState<CEMutationResponse, (instanceId: string) => Promise<CEMutationResponse>>;
  reactivateCE: AsyncState<CEMutationResponse, (instanceId: string) => Promise<CEMutationResponse>>;
  deleteLinkedInstance: AsyncState<void, (instanceId: string) => Promise<void>>;
}

const TaniumInstancesContext = createContext<TaniumInstancesCtx>({} as TaniumInstancesCtx);

export const TaniumInstancesProvider: React.FC = ({ children }) => {
  const taniumInstancesHook = useTaniumInstancesHook();
  return (
    <TaniumInstancesContext.Provider value={{ ...taniumInstancesHook }}>{children}</TaniumInstancesContext.Provider>
  );
};

export function useTaniumInstances(): TaniumInstancesCtx {
  return useContext(TaniumInstancesContext);
}

const useTaniumInstancesHook = () => {
  const [allLinkedTaniumInstances, setAllLinkedTaniumInstances] = useState<TaniumInstance[]>([]);
  const apiClient = useAuthenticatedApiContext();

  const linkedTaniumInstances = allLinkedTaniumInstances;

  const loadLinkedTaniumInstances = useAsync(
    useCallback((): Promise<void> => {
      // Available (pending) rows surface first as the actionable ones; the rest sort by linked date
      // desc. Pending rows have a zero createdAt, so they would otherwise sink.
      return apiClient.getLinkedInstances().then((res) => {
        const byLinkedDateDesc = [...res].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        setAllLinkedTaniumInstances([
          ...byLinkedDateDesc.filter((i) => i.isAvailable),
          ...byLinkedDateDesc.filter((i) => !i.isAvailable),
        ]);
      });
    }, [apiClient]),
    { autoReset: true }
  );
  useAsyncEffect(loadLinkedTaniumInstances, []);

  const deactivateCE = useAsync(
    useCallback(
      async (instanceId: string): Promise<CEMutationResponse> => {
        const result = await apiClient.deactivateCE(instanceId);
        await loadLinkedTaniumInstances.execute();
        return result;
      },
      [apiClient, loadLinkedTaniumInstances]
    ),
    { autoReset: true }
  );

  const reactivateCE = useAsync(
    useCallback(
      async (instanceId: string): Promise<CEMutationResponse> => {
        const result = await apiClient.reactivateCE(instanceId);
        await loadLinkedTaniumInstances.execute();
        return result;
      },
      [apiClient, loadLinkedTaniumInstances]
    ),
    { autoReset: true }
  );

  const deleteLinkedInstance = useAsync(
    useCallback(
      async (instanceId: string): Promise<void> => {
        await apiClient.removeDeletedInstance(instanceId);
        await loadLinkedTaniumInstances.execute();
      },
      [apiClient, loadLinkedTaniumInstances]
    ),
    { autoReset: true }
  );

  return {
    loadLinkedTaniumInstances,
    linkedTaniumInstances,
    deactivateCE,
    reactivateCE,
    deleteLinkedInstance,
  };
};
